import { defineStore } from "pinia";
import { computed } from "vue";
import { useBaseFormStore } from "@/shared/stores/baseFormStore";
import type { FormDataItem, FormItem, InputDataItem } from "@/shared/types";
import { createPrefixHandler } from "@/shared/utils/prefixHandler";

export const useFormDataStore = defineStore("formData", () => {
	// InteractForm用のプレフィックスハンドラーを作成
	const prefixHandler = createPrefixHandler(/^([a-z]+):/i);

	// 共通ベースストアを使用
	const baseStore = useBaseFormStore();

	// ベースストアの機能を再エクスポート（computedを使用してリアクティビティを確保）
	const formItems = computed(() => baseStore.formItems);
	const contactFormId = computed(() => baseStore.contactFormId);
	const formDetails = computed(() => baseStore.formDetails);
	const currentQuestionIndex = computed(() => baseStore.currentQuestionIndex);
	const hasCompletedAllQuestions = computed(
		() => baseStore.hasCompletedAllQuestions,
	);
	const currentQuestion = computed(() => baseStore.currentQuestion);
	const totalQuestions = computed(() => baseStore.totalQuestions);
	const isAnswerEmpty = computed(() => baseStore.isAnswerEmpty);
	const isNextButtonDisabled = computed(() => baseStore.isNextButtonDisabled);
	const baseNextQuestion = baseStore.nextQuestion;
	const basePrevQuestion = baseStore.prevQuestion;
	const setContactFormId = baseStore.setContactFormId;
	const initializeData = baseStore.initializeData;
	const baseUpdateAnswer = baseStore.updateAnswer;
	const baseResetForm = baseStore.resetForm;
	const setCompletedAllQuestions = baseStore.setCompletedAllQuestions;

	// インタラクトフォーム固有のロジックを持つnextQuestion
	function nextQuestion() {
		// ベースストアのnextQuestionを呼び出し
		baseNextQuestion();
	}

	// インタラクトフォーム固有のロジックを持つprevQuestion
	function prevQuestion() {
		// ベースストアのprevQuestionを呼び出し
		basePrevQuestion();
	}

	// 入力データからフォームデータを初期化する関数
	function initializeFromInputData(inputData: InputDataItem[], formId: string) {
		if (!inputData || !Array.isArray(inputData) || inputData.length === 0) {
			console.error("[FormDataStore] Invalid or empty input data:", inputData);
			// Initialize with empty arrays to prevent undefined errors
			baseStore.initializeData([], []);
			return null;
		}

		try {
			// contactFormIdを設定
			setContactFormId(formId);

			// 重複IDを検出し、最初の設問のみを保持する
			const seenIds = new Set<number>(); // 既に処理したID
			const uniqueInputData: InputDataItem[] = [];

			// 重複を除外した入力データを作成
			inputData.forEach((item) => {
				if (!seenIds.has(item.id)) {
					// 初めて見るIDの場合は追加
					seenIds.add(item.id);
					uniqueInputData.push(item);
				} else {
					// 重複IDを検出した場合はスキップ
					console.warn(
						`重複したID ${item.id} を検出しました。この設問はスキップします。`,
						item,
					);
				}
			});

			// 基本データの作成（重複を除外したデータを使用）
			const basicData = uniqueInputData.map((item) => ({
				id: item.id,
				name: item.name || `question_${item.id}`,
				value: "", // 初期値は空
			}));

			// 詳細データの作成（重複を除外したデータを使用）
			const detailsData = uniqueInputData
				.map((item) => {
					try {
						// 必要なプロパティが存在するか確認
						if (!item || !item.type || !item.data) {
							console.warn("Skipping item with missing properties:", item);
							return null;
						}

						// FormItem形式に変換
						const formItem: FormItem = {
							id: item.id,
							label: item.label || "",
							question: item.question || item.label || "",
							type: item.type,
							data: Array.isArray(item.data) ? item.data : [],
							required: item.required || false,
							answer:
								item.type === "check_box" || item.type === "prefectures"
									? []
									: "",
							is_trigger: item.is_trigger || false,
							prefer_referral: !!item.prefer_referral,
							related_service: item.related_service,
							exclude_services: item.exclude_services,
							selectedService: {},
							images: item.images,
						};

						return formItem;
					} catch (e) {
						console.error("Failed to process item:", item, e);
						return null;
					}
				})
				.filter((item): item is FormItem => item !== null);

			// ストアのデータを初期化
			baseStore.initializeData(basicData, detailsData);

			return {
				basicData,
				detailsData,
			};
		} catch (e) {
			console.error("Error initializing form data:", e);
			return null;
		}
	}

	// データを初期化する関数は削除 - baseStoreのinitializeDataを使用

	// データを追加する関数
	function addItem(item: FormDataItem) {
		formItems.value.push(item);
	}

	// データを更新する関数
	function updateItem(id: number, updatedItem: Partial<FormDataItem>) {
		const index = formItems.value.findIndex((item) => item.id === id);
		if (index !== -1) {
			formItems.value[index] = { ...formItems.value[index], ...updatedItem };
		}
	}

	// インタラクトフォーム固有のロジックを持つupdateAnswer
	function updateAnswer(id: number, answer: string | string[]) {
		// まずベースストアのupdateAnswerを呼び出し
		baseUpdateAnswer(id, answer);

		// インタラクトフォーム固有のトリガーロジック
		if (!formDetails.value || !Array.isArray(formDetails.value)) {
			console.warn(
				"[FormDataStore] formDetails is not initialized properly:",
				formDetails.value,
			);
			return;
		}

		const detailIndex = formDetails.value.findIndex((item) => item.id === id);
		if (detailIndex !== -1) {
			// is_triggerがtrueの場合、関連サービスを保持
			if (
				formDetails.value[detailIndex].is_trigger &&
				formDetails.value[detailIndex].related_service
			) {
				const currentItem = formDetails.value[detailIndex];
				const relatedService = currentItem.related_service;
				const selectedService: Record<string, number[]> = {};

				if (currentItem.type === "prefectures" && Array.isArray(answer)) {
					const serviceSets: Record<string, Set<number>> = {};
					answer.forEach((prefectureValue) => {
						const answerIndex = currentItem.data.findIndex(
							(item) => item === prefectureValue,
						);
						if (answerIndex === -1 || !relatedService) {
							return;
						}

						Object.keys(relatedService).forEach((key) => {
							const value = relatedService[key];
							if (Array.isArray(value) && value.length > answerIndex) {
								if (!serviceSets[key]) {
									serviceSets[key] = new Set<number>();
								}
								serviceSets[key].add(value[answerIndex]);
							}
						});
					});

					Object.keys(serviceSets).forEach((key) => {
						selectedService[key] = Array.from(serviceSets[key]);
					});

					formDetails.value[detailIndex].selectedService = selectedService;
					return;
				}

				const answerStr = Array.isArray(answer) ? answer[0] : answer;
				// データ配列から選択された回答のインデックスを取得
				const answerIndex = currentItem.data.findIndex(
					(item) => item === answerStr,
				);

				// インデックスが見つかった場合、対応するサービスを選択
				if (answerIndex !== -1) {
					// 任意のアルファベットプレフィックスをサポート
					if (relatedService) {
						// relatedServiceのすべてのキーをループ処理
						Object.keys(relatedService).forEach((key) => {
							const value = relatedService[key];
							// 配列の場合のみ処理
							if (Array.isArray(value) && value.length > answerIndex) {
								selectedService[key] = [value[answerIndex]];
							}
						});
					}

					formDetails.value[detailIndex].selectedService = selectedService;
				}
			}
		}
	}

	// データを削除する関数
	function removeItem(id: number) {
		const index = formItems.value.findIndex((item) => item.id === id);
		if (index !== -1) {
			formItems.value.splice(index, 1);
		}
	}

	// メール送信データ処理
	function processFormData() {
		// 現在のページタイトルを取得（グローバル変数から取得）
		const pageTitle = window.getBizcanPageTitle
			? window.getBizcanPageTitle()
			: window.document.title || "";

		// トリガー設問と選択されたサービスの情報を収集
		const triggerQuestionsData = formDetails.value
			.filter((item) => item.is_trigger && item.selectedService)
			.map((item) => {
				// 選択された回答を取得（配列または単一値）
				const answers = Array.isArray(item.answer)
					? item.answer
					: [item.answer || ""];

				// 各回答のプレフィックスと表示用テキストを処理
				const processedAnswers = answers.map((ans) => {
					const prefix = prefixHandler.extractPrefix(ans);
					const display = ans ? prefixHandler.removePrefix(ans) : ans;
					return { original: ans, prefix, display };
				});

				// サービスIDを取得（選択された回答の先頭文字に対応するもの）
				const serviceIds: Record<string, number[]> = {};

				// 各回答のプレフィックスに基づいてサービスIDを収集
				processedAnswers.forEach((ans) => {
					if (ans.prefix && item.selectedService) {
						const prefix = ans.prefix;

						// 複数文字のプレフィックスを処理（例：'ab'の場合、'a'と'b'の両方のサービスIDを取得）
						if (prefix.length > 1) {
							// 各文字を個別に処理
							for (let i = 0; i < prefix.length; i++) {
								const singlePrefix = prefix[i];
								if (item.selectedService[singlePrefix]) {
									serviceIds[singlePrefix] = serviceIds[singlePrefix] || [];
									serviceIds[singlePrefix] = [
										...(serviceIds[singlePrefix] || []),
										...(item.selectedService[singlePrefix] || []),
									];
								}
							}
						}
						// 単一文字のプレフィックスの場合（従来の処理）
						else if (item.selectedService[prefix]) {
							serviceIds[prefix] = serviceIds[prefix] || [];
							serviceIds[prefix] = [
								...(serviceIds[prefix] || []),
								...(item.selectedService[prefix] || []),
							];
						}
					}
				});

				// 重複を排除
				Object.keys(serviceIds).forEach((key) => {
					if (serviceIds[key]) {
						serviceIds[key] = [...new Set(serviceIds[key])];
					}
				});

				// プレフィックスの存在確認（少なくとも1つの回答にプレフィックスがあるか）

				return {
					questionId: item.id,
					question: item.question,
					answer: answers.join(", "),
					displayAnswer: processedAnswers.map((a) => a.display).join(", "),
					answerPrefix: processedAnswers.some((a) => a.prefix !== null)
						? processedAnswers[0].prefix
						: null,
					serviceIds: serviceIds,
					// 複数選択用の追加情報
					isMultipleAnswers: answers.length > 1,
					originalAnswers: answers,
					processedAnswers: processedAnswers,
				};
			});

		// トリガー設問と選択サービスの情報をテキストで整形（プレーンテキスト版 - エスケープ済み）
		const triggersText = triggerQuestionsData
			.map((item) => {
				const serviceIdsText =
					Object.entries(item.serviceIds).length > 0
						? Object.entries(item.serviceIds)
								.map(([key, ids]) => `${key}: ${ids.join(", ")}`)
								.join("; ")
						: "関連サービスなし";

				// 複数選択の場合は各回答の詳細情報を表示
				let answersText = "";
				if (item.isMultipleAnswers) {
					answersText = item.processedAnswers
						.map((ans) => {
							const originalText = ans.prefix
								? `(元の値: ${ans.original})`
								: "";
							return `- ${ans.display} ${originalText}`;
						})
						.join("\n");
				} else {
					const originalValueText = item.answerPrefix
						? `(元の値: ${item.answer})`
						: "";
					answersText = `${item.displayAnswer} ${originalValueText}`;
				}

				return `トリガー設問: ${item.question}\n選択された回答:\n${answersText}\n関連サービスID: ${serviceIdsText}`;
			})
			.join("\n\n");

		// HTML版のトリガー情報（メール用）
		const triggersHtml = triggerQuestionsData
			.map((item) => {
				const serviceIdsText =
					Object.entries(item.serviceIds).length > 0
						? Object.entries(item.serviceIds)
								.map(([key, ids]) => `${key}: ${ids.join(", ")}`)
								.join("; ")
						: "関連サービスなし";

				// 複数選択の場合は各回答の詳細情報を表示
				let answersHtml = "";
				if (item.isMultipleAnswers) {
					answersHtml =
						"<ul>" +
						item.processedAnswers
							.map((ans) => {
								const originalText = ans.prefix
									? `(元の値: ${ans.original})`
									: "";
								return `<li>${ans.display} ${originalText}</li>`;
							})
							.join("") +
						"</ul>";
				} else {
					const originalValueText = item.answerPrefix
						? `(元の値: ${item.answer})`
						: "";
					answersHtml = `${item.displayAnswer} ${originalValueText}`;
				}

				return `<p><strong>トリガー設問:</strong> ${item.question}</p>
<p><strong>選択された回答:</strong></p>
${answersHtml}
<p><strong>関連サービスID:</strong> ${serviceIdsText}</p>`;
			})
			.join("<hr>");

		// メール用のプレーンテキスト版（改行はそのまま、HTMLタグなし）
		const triggersPlainText = triggerQuestionsData
			.map((item) => {
				const serviceIdsText =
					Object.entries(item.serviceIds).length > 0
						? Object.entries(item.serviceIds)
								.map(([key, ids]) => `${key}: ${ids.join(", ")}`)
								.join("; ")
						: "関連サービスなし";

				// 複数選択の場合は各回答の詳細情報を表示
				let answersText = "";
				if (item.isMultipleAnswers) {
					answersText = item.processedAnswers
						.map((ans) => {
							const originalText = ans.prefix
								? `(元の値: ${ans.original})`
								: "";
							return `- ${ans.display} ${originalText}`;
						})
						.join("\n");
				} else {
					const originalValueText = item.answerPrefix
						? `(元の値: ${item.answer})`
						: "";
					answersText = `${item.displayAnswer} ${originalValueText}`;
				}

				return `トリガー設問: ${item.question}
選択された回答:
${answersText}
関連サービスID: ${serviceIdsText}`;
			})
			.join("\n\n");

		// デフォルトテキスト設定
		const finalTriggersText =
			triggersText || "トリガー設問による選択はありません";
		const finalTriggersHtml =
			triggersHtml || "<p>トリガー設問による選択はありません</p>";
		const finalTriggersPlainText =
			triggersPlainText || "トリガー設問による選択はありません";

		// 設問と回答のテキストを生成（HTMLタグを使用）
		const formattedHtml = formDetails.value
			.map((item) => {
				let answer: string;
				if (item.type === "prefectures" && Array.isArray(item.answer)) {
					answer = item.answer
						.map((value: string) => prefixHandler.removePrefix(value))
						.join(", ");
				} else if (Array.isArray(item.answer)) {
					answer = item.answer.join(", ");
				} else {
					answer = item.answer || "未回答";
				}

				// トリガー設問の場合は、表示用の回答テキストを使用
				if (item.is_trigger && typeof answer === "string" && answer) {
					answer = prefixHandler.removePrefix(answer);
				}

				return `<p><strong>${item.question}:</strong> ${answer}</p>`;
			})
			.join("");

		// プレーンテキスト版も生成（改行を含む）
		const plainText = formDetails.value
			.map((item) => {
				let answer: string;
				if (Array.isArray(item.answer)) {
					answer = item.answer
						.map((value: string) => prefixHandler.removePrefix(value))
						.join(", ");
				} else {
					answer = item.answer ? prefixHandler.removePrefix(item.answer) : "未回答";
				}

				return `${item.question}\n⇒${answer}`;
			})
			.join("\n\n");

		// 改行文字をエスケープ（WordPressのメール送信で\nとして処理されるように）
		const escapedText = plainText.replace(/\n/g, "\\n");

		// 元の返り値に加えて、新しい形式のデータも追加
		return {
			contactFormId: contactFormId.value,
			formData: formItems.value,
			questionsAndAnswersText: plainText,
			escapedQuestionsAndAnswersText: escapedText,
			formattedHtml: formattedHtml,
			pageTitle: pageTitle,
			selectedServices: triggerQuestionsData,
			triggersText: finalTriggersText.replace(/\n/g, "\\n"),
			triggersHtml: finalTriggersHtml,
			triggersPlainText: finalTriggersPlainText,
		};
	}

	// フォームをリセットする関数
	function resetForm() {
		// formItemsの値をリセット
		formItems.value.forEach((item) => {
			item.value = "";
		});

		// formDetailsの回答をリセット
		formDetails.value.forEach((item) => {
			item.answer = item.type === "check_box" ? [] : "";
			item.selectedService = {}; // 選択されたサービスもリセット
		});

		// Use base store's resetForm to reset these values
		baseResetForm();
	}

	return {
		formItems,
		formDetails,
		contactFormId,
		currentQuestionIndex,
		currentQuestion,
		totalQuestions,
		hasCompletedAllQuestions,
		isAnswerEmpty,
		isNextButtonDisabled,
		setContactFormId,
		initializeFromInputData,
		initializeData: baseStore.initializeData,
		addItem,
		updateItem,
		updateAnswer,
		removeItem,
		processFormData,
		nextQuestion,
		prevQuestion,
		resetForm,
		setCompletedAllQuestions,
	};
});
